/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1712534400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Apr 08 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "zendesk",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Supported objects",
          "depth": 1,
          "id": "supported-objects"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Relationship records",
          "depth": 1,
          "id": "relationship-records"
        }, {
          "value": "Search batch size",
          "depth": 1,
          "id": "search-batch-size"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      img: "img",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      h3: "h3",
      a: "a",
      h4: "h4"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to Admin Settings > Apps and integrations > Zendesk API and add a new API token for Hightouch. Make sure you enable ", _jsx(_components.em, {
          children: "Token access"
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-api-token.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Copy the API token and fill in the fields in Hightouch destination setup:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API token:"
          }), " token from Zendesk"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email:"
          }), " email used to login to Zendesk"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Support URL:"
          }), " URL for your Zendesk support site. make sure to include the ", _jsx(_components.code, {
            children: "https://"
          }), " (ex: ", _jsx(_components.code, {
            children: "https://hightouch.zendesk.com"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.h3, {
        id: "supported-objects",
        children: "Supported objects"
      }), "\n", _jsx(_components.p, {
        children: "This integration support syncing the following objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Users"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Organizations"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Tickets"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Custom Objects"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Legacy Custom Objects"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Organization Memberships"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Zendesk has ", _jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/5914453843994-Understanding-custom-objects",
          children: "custom objects"
        }), " and ", _jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/4408834725402",
          children: "legacy custom objects"
        }), ". If your Zendesk account has any custom objects, they show up in the objects dropdown.\nCustom objects have a set prefix: ", _jsx(_components.strong, {
          children: "(Custom)"
        }), ". Legacy custom objects have a set prefix: ", _jsx(_components.strong, {
          children: "(Legacy custom)"
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-custom-objects.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Zendesk and updates fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing objects in Zendesk. It doesn't add new objects."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": pushes new objects to Zendesk. It doesn't update any objects. (Only available for ", _jsx(_components.code, {
            children: "Organization Memberships"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your Zendesk objects by selecting a column in your results that matches the external ID, Zendesk ID, email (", _jsx(_components.em, {
          children: "contact only"
        }), "), or custom fields of the Zendesk object."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "organization-memberships",
        children: "Organization memberships"
      }), "\n", _jsxs(_components.p, {
        children: ["Organization memberships will behave differently. These objects will be matched by both the ", _jsx(_components.code, {
          children: "user_id"
        }), " ", _jsx(_components.em, {
          children: "and"
        }), " ", _jsx(_components.code, {
          children: "organization_id"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-org-membership-id.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This field should be a combination of your user and organization columns that you will use in the mappings section. For example,\nif you are using the user's ", _jsx(_components.code, {
          children: "email"
        }), " and the organization's ", _jsx(_components.code, {
          children: "external_id"
        }), ", this field should be: ", _jsx(_components.code, {
          children: "user_email:org_externalId"
        }), ". Do not edit/change\neither the user or organization field. Instead, you will need to remove the old row and create a new one to represent a new organization membership.\nYour model should look similar to below:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-org-membership-query-ex.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to Zendesk's default fields, as well as custom fields for your users, organizations and tickets objects. For more info on how to add custom fields to users, refer to the ", _jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/203662066",
          children: "Zendesk docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "For custom objects, you will be able to sync to any properties from your defined schema."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-custom-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "relationship-records",
        children: "Relationship records"
      }), "\n", _jsxs(_components.p, {
        children: ["You can create associations between existing object records using a predetermined relationship type. The associations can be found in the field mappings. For more details, you can refer to the ", _jsx(_components.a, {
          href: "https://developer.zendesk.com/api-reference/custom-data/custom-objects-api/relationships",
          children: "Zendesk docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "search-batch-size",
        children: "Search batch size"
      }), "\n", _jsx(_components.p, {
        children: "Zendesk's search API can return up to 1000 results, but by default Hightouch only searches for 100 records\nat a time. You can increase this amount up to 500 for a faster sync."
      }), "\n", _jsxs(_components.p, {
        children: ["However, increasing the batch size\ncan increase the risk of receiving a ", _jsx(_components.a, {
          href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/414",
          children: "'414 - Long URI`"
        }), " error. Conversely, if you are experiencing these errors\nwith the default batch size setting, you can lower the batch size to avoid the errors."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-batch-form.png",
          alt: "Setting batch size in the Hightouch UI"
        })
      })]
    });
  }
}
export default MDXContent;
